import {
  Cloud,
  MachineLearning as Brain,
  Lightning as Zap,
  DataBase as Database,
  Locked as Lock,
  Renew as RefreshCw,
  Layers,
  Activity,
  ChartColumn as BarChart3,
  Target,
  Group as Users,
  Chip as Cpu,
  BlockStorage as HardDrive,
  Branch as GitBranch,
  Growth as TrendingUp,
  WarningAlt as AlertTriangle,
  Idea as Lightbulb,
  Book as BookOpen,
  Badge as Award,
  Meter as Gauge,
  Rocket,
  Chip as CircuitBoard,
  SettingsAdjust as SlidersHorizontal,
  Security as Shield,
  Wifi,
  Globe,
  Scan as ScanEye,
} from '@carbon/icons-react'

/**
 * Authored solutions copy, moved out of app/solutions/[slug]/page.tsx verbatim.
 * Nothing here was retyped or reworded — the bespoke solution routes and the
 * legacy [slug] fallback both read from this one module, so the copy cannot
 * drift between them.
 */

// HERO VIDEOS
// ─────────────────────────────────────────────────────────────────────────────
/* Only the /solutions/[slug] fallback reads this now. cloud-infrastructure has
   no entry: its static route renders the engraved-plate hero, which uses no
   video, and the fallback tolerates a missing key. */
export const HERO_VIDEOS: Record<string, { src: string; poster: string }> = {
  'ai-infrastructure': {
    src: '/solutions/ai_hero.mp4',
    // Was /solutions/ai_slide1.jpg, which does not exist in /public — the AI
    // hero video has been shipping with a broken poster. heroai.jpg is the
    // real AI hero still that is present.
    poster: '/solutions/heroai.webp',
  },
}

// ─────────────────────────────────────────────────────────────────────────────
// IMAGES
// ─────────────────────────────────────────────────────────────────────────────
export const IMAGES = {
  cloud: {
    overview: '/solutions/cloud_overview.webp',
    howItWorks: '/solutions/cloud_work.webp',
    capabilities: '/solutions/cloud_team.webp',
    closing: '/solutions/close_cloud.webp',
    useCase0: '/solutions/clouduse1.webp',
    useCase1: '/solutions/clouduse2.webp',
    useCase2: '/solutions/clouduse3.webp',
    useCase3: '/solutions/clouduse4.webp',
  },
  ai: {
    overview: '/solutions/overviewai2.webp',
    howItWorks: '/solutions/ai_pipeline.webp',
    capabilities: '/solutions/ai_product.webp',
    closing: '/solutions/closeai.webp',
    useCase0: '/solutions/aiuse1.webp',
    useCase1: '/solutions/aiuse_2.webp',
    useCase2: '/solutions/aiuse_3.webp',
    useCase3: '/solutions/aiuse_4.webp',
  },
}

// ─────────────────────────────────────────────────────────────────────────────
// SOLUTIONS DATA
// ─────────────────────────────────────────────────────────────────────────────
export const SOLUTIONS: Record<string, any> = {
  'cloud-infrastructure': {
    slug: 'cloud-infrastructure',
    number: '01',
    title: 'Cloud Infrastructure',
    /* `heading` is the page h1 only. `title` stays the short label the JSON-LD
       name, image alt text and card copy read, so naming the audience in the
       headline does not leak into every other use of the name. */
    heading: 'Cloud Infrastructure for Educational Institutions',
    subtitle: 'The Backbone of Unified Digital Campuses',
    tagline:
      'True digital transformation begins with resilient infrastructure.',
    heroIcon: Cloud,
    images: IMAGES.cloud,
    heroTheme: 'cloud',
    intro: `In today's digital-first academic environment, infrastructure determines performance. At Euclideum, cloud infrastructure is not simply an operational decision it is a strategic foundation that powers everything within the Unified Digital Campus (UDC).`,
    body1: `Every module within UDC from examination management and admissions processing to learning delivery and AI-driven analytics operates on scalable, enterprise-grade cloud architecture ensuring uninterrupted access, consistent performance, and real-time responsiveness even during peak academic cycles.`,
    body2: `Educational ecosystems cannot afford downtime. Examinations must run without disruption. Fee transactions must process securely. Admissions workflows must remain accessible under high traffic. Our cloud infrastructure is built to handle these mission-critical demands with high availability and elastic scalability.`,
    body3: `Beyond stability, the cloud enables intelligent connectivity. Student data, academic performance metrics, financial records, and administrative workflows operate within a unified ecosystem ensuring consistent data flow and institutional transparency.`,
    closing: `For Euclideum, cloud infrastructure is more than hosting it is the invisible engine that empowers institutions to scale confidently, innovate continuously, and operate without limitations.`,
    deliversHeading: 'What Our Cloud Architecture',
    deliversAccent: 'Delivers',
    delivers: [
      {
        icon: Zap,
        title: 'Peak Scalability',
        text: 'Enterprise-grade auto-scaling during high-traffic examination and admission periods without performance degradation.',
      },
      {
        icon: Activity,
        title: 'High Availability',
        text: 'Redundant architecture with 99.9% uptime SLA ensuring consistent service continuity across all institutional workflows.',
      },
      {
        icon: Lock,
        title: 'Encrypted Security',
        text: '256-bit end-to-end encrypted data environments meeting the strictest institutional compliance standards.',
      },
      {
        icon: Users,
        title: 'Role-Based Access',
        text: 'Granular access control across all departments, roles, and administrative layers the right people see the right data.',
      },
      {
        icon: RefreshCw,
        title: 'Disaster Recovery',
        text: 'Automated backups and rapid recovery protocols protecting institutional data with zero tolerance for loss.',
      },
      {
        icon: Layers,
        title: 'Unified Integration',
        text: 'Seamless connectivity between LMS, HRMS, Accounts, and all core UDC systems within one coherent cloud ecosystem.',
      },
    ],
    howItWorks: [
      {
        icon: GitBranch,
        step: '01',
        title: 'Architecture Design',
        text: 'We architect a cloud environment tailored to your institutional scale, workflow complexity, and compliance requirements.',
      },
      {
        icon: HardDrive,
        step: '02',
        title: 'Secure Data Migration',
        text: 'Existing data is migrated securely with zero data loss, maintaining full continuity throughout the entire transition.',
      },
      {
        icon: Wifi,
        step: '03',
        title: 'System Integration',
        text: 'All UDC modules are connected within the unified cloud ecosystem, enabling seamless real-time data flow.',
      },
      {
        icon: Gauge,
        step: '04',
        title: 'Ongoing Monitoring',
        text: 'Continuous monitoring dashboards give institutional leadership full visibility into system health and performance.',
      },
    ],
    useCases: [
      {
        icon: ScanEye,
        title: 'Examination Management',
        text: 'Run high-stakes examinations for thousands of concurrent students without any performance degradation or downtime.',
      },
      {
        icon: Users,
        title: 'Admissions Surge',
        text: 'Handle massive application volumes during peak admission periods with elastic auto-scaling infrastructure.',
      },
      {
        icon: Shield,
        title: 'Fee Processing',
        text: 'Secure, high-throughput payment processing fully integrated with financial management and accounting systems.',
      },
      {
        icon: Database,
        title: 'Data Warehousing',
        text: 'Centralised institutional data lake enabling powerful cross-module analytics, reporting, and business intelligence.',
      },
    ],
  },
  'ai-infrastructure': {
    slug: 'ai-infrastructure',
    badge: 'Artificial Intelligence',
    number: '02',
    title: 'AI Infrastructure',
    heading: 'AI Infrastructure for Educational Institutions',
    subtitle: 'Powering Intelligent Education at Scale',
    tagline: 'AI is not automation it is augmentation.',
    heroIcon: Brain,
    images: IMAGES.ai,
    heroTheme: 'ai',
    intro: `Artificial Intelligence should not sit on top of a system it should live within it. At Euclideum, AI is embedded at the infrastructure level of the Unified Digital Campus (UDC), forming the intelligence layer that transforms institutional data into meaningful, actionable insight.`,
    body1: `Every academic interaction contributes to a continuously evolving ecosystem. Assessments, attendance patterns, learning engagement, progression trends, and placement outcomes are not isolated data points they become signals within a learning intelligence network designed to improve outcomes in real time.`,
    body2: `Our AI infrastructure is built for both scale and precision. Advanced machine learning models identify performance patterns, detect early indicators of academic risk, and recommend timely interventions before challenges escalate. Adaptive learning engines dynamically personalize content pathways enabling students to progress based on strengths.`,
    body3: `For institutional leadership, AI-driven analytics convert complex datasets into predictive visibility. Administrators gain insights into academic bottlenecks, operational inefficiencies, and placement probability trends. Faculty receive structured feedback on learner comprehension enabling evidence-based teaching strategies.`,
    closing: `Security and responsible AI remain foundational. Data privacy, algorithmic transparency, and institutional control over insights ensure ethical deployment at every level. At Euclideum, AI infrastructure empowers educators, guides students, and enables institutions to make smarter, faster, and more confident decisions at scale.`,
    deliversHeading: 'What Our AI Infrastructure',
    deliversAccent: 'Enables',
    delivers: [
      {
        icon: BarChart3,
        title: 'Predictive Analytics',
        text: 'ML models forecast student performance trends before they become visible through traditional assessment methods.',
      },
      {
        icon: AlertTriangle,
        title: 'Early Risk Detection',
        text: 'Identify at-risk learners weeks before critical milestones enabling timely, targeted, and effective interventions.',
      },
      {
        icon: SlidersHorizontal,
        title: 'Adaptive Learning',
        text: "Dynamic content pathways that continuously adjust to each student's learning velocity and comprehension patterns.",
      },
      {
        icon: TrendingUp,
        title: 'Placement Forecasting',
        text: 'Data-driven placement probability models aligned with live industry hiring patterns and verified skill benchmarks.',
      },
      {
        icon: Globe,
        title: 'Institutional Dashboards',
        text: 'Real-time leadership visibility across academic performance, operational metrics, and long-term outcome indicators.',
      },
      {
        icon: CircuitBoard,
        title: 'Cross-Module AI',
        text: 'Unified AI intelligence layer operating seamlessly across all UDC modules from admissions to alumni outcomes.',
      },
    ],
    howItWorks: [
      {
        icon: Database,
        step: '01',
        title: 'Data Ingestion',
        text: 'All student interactions, assessments, attendance, and engagement signals are captured into a unified structured data pipeline.',
      },
      {
        icon: Cpu,
        step: '02',
        title: 'Model Training',
        text: "Institution-specific ML models are trained and continuously updated using your own academic ecosystem's real data.",
      },
      {
        icon: Lightbulb,
        step: '03',
        title: 'Insight Generation',
        text: 'AI translates raw signals into actionable alerts, forecasts, and recommendations surfaced for faculty and leadership.',
      },
      {
        icon: Rocket,
        step: '04',
        title: 'Continuous Learning',
        text: 'Models evolve with each academic cycle becoming progressively more accurate, contextual, and institutionally relevant.',
      },
    ],
    useCases: [
      {
        icon: BookOpen,
        title: 'Faculty Intelligence',
        text: 'Faculty dashboards surface comprehension gaps, engagement dips, and targeted intervention recommendations per student cohort.',
      },
      {
        icon: Target,
        title: 'Student Guidance',
        text: 'AI-powered nudges guide individual students toward the right resources, milestones, and support at precisely the right moment.',
      },
      {
        icon: Award,
        title: 'Placement Readiness',
        text: 'Skill gap analysis aligned with live industry benchmarks prepares every student for measurable career success and placement.',
      },
      {
        icon: Gauge,
        title: 'Outcome Prediction',
        text: 'Institutional leaders gain multi-semester visibility into academic outcome trajectories and cohort progression trends.',
      },
    ],
  },
}
